import type { ReactNode } from "react";

import type { MeterBarProps } from "./meter-bar";

import clsx from "clsx";

import { MeterBar } from "./meter-bar";

import styles from "./meter.module.css";

/**
 * Label and Value are shown as aria information
 */
export type MeterSegment = {
	id: string;
	label: string;
	value: number;
};

type MeterBarItem = MeterBarProps & {
	id: string;
};

const CHART_SEQUENCE = [
	styles.chart1,
	styles.chart2,
	styles.chart3,
	styles.chart4,
	styles.chart5,
	styles.chart6,
	styles.chart7,
	styles.chart8,
];

function toSequenceClassName(index: number): string {
	return CHART_SEQUENCE[index % CHART_SEQUENCE.length];
}

type MeterProps = {
	className?: string;
	label: string;
	maxValue?: number;
	remainderLabel?: string;
	segments: MeterSegment[];
};

function toShare(value: number, maxValue: number): number {
	if (maxValue <= 0) {
		return 0;
	}

	return Math.min(100, Math.max(0, (value / maxValue) * 100));
}

function formatShare(value: number, maxValue: number): string {
	const share = toShare(value, maxValue);

	if (share > 0 && share < 1) {
		return "<1%";
	}

	return `${Math.round(share * 10) / 10}%`;
}

function describe(label: string, value: number, maxValue: number): string {
	return `${label} ${formatShare(value, maxValue)}`;
}

export function Meter({
	className,
	label,
	maxValue = 100,
	remainderLabel = "Unallocated",
	segments,
}: MeterProps): ReactNode {
	const allocated = segments.reduce((total, segment) => {
		return total + Math.max(0, segment.value);
	}, 0);
	const remainder = Math.max(0, maxValue - allocated);
	const bars: MeterBarItem[] = segments.map((segment, index) => {
		return {
			description: describe(segment.label, segment.value, maxValue),
			id: segment.id,
			share: toShare(segment.value, maxValue),
			toneClassName: toSequenceClassName(index),
		};
	});

	if (toShare(remainder, maxValue) > 0) {
		bars.push({
			description: describe(remainderLabel, remainder, maxValue),
			id: "remainder",
			isRemainder: true,
			share: toShare(remainder, maxValue),
			toneClassName: styles.remainder,
		});
	}

	return (
		<div className={clsx(styles.meter, className)}>
			<div className={styles.header}>
				<span className={clsx("caption", styles.label)}>{label}</span>
				<span className={clsx("caption", "bold", styles.total)}>
					{formatShare(allocated, maxValue)}
				</span>
			</div>

			<div className={styles.track}>
				{bars.map((bar) => {
					return <MeterBar key={bar.id} {...bar} />;
				})}
			</div>
		</div>
	);
}
